import React, { useEffect, useState } from "react";
import RichTextEditor from "./RichTextEditor";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import api from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { ISSUE_TYPES, PRIORITIES, STATUSES } from "@/lib/meta";
import { toast } from "sonner";
import { Paperclip, X } from "lucide-react";

export default function CreateIssueDialog({ open, setOpen, projects, users, onCreated, defaultProjectId, defaultSprintId, defaultEpicId }) {
  const { issueTypes, priorities, statuses } = useSettings() || {};
  const TYPES = (issueTypes && issueTypes.length) ? issueTypes : ISSUE_TYPES;
  const PRIOS = (priorities && priorities.length) ? priorities : PRIORITIES;
  const STATUS_LIST = (statuses && statuses.length) ? statuses : STATUSES;

  const [form, setForm] = useState({
    project_id: "", summary: "", description: "", issue_type: "task", status: "todo", priority: "medium",
    assignee_id: "unassigned", sprint_id: "none", epic_id: null, story_points: "",
  });
  const [sprints, setSprints] = useState([]);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(f => ({
        ...f,
        project_id: defaultProjectId || projects[0]?.id || "",
        sprint_id: defaultSprintId || "none",
        epic_id: defaultEpicId || null,
        status: "todo",
        summary: "", description: "",
      }));
      setFiles([]);
    }
  }, [open, projects, defaultProjectId, defaultSprintId, defaultEpicId]);

  useEffect(() => {
    if (form.project_id) {
      api.get(`/sprints?project_id=${form.project_id}`)
        .then(r => setSprints(r.data || []))
        .catch(() => setSprints([]));
    } else {
      setSprints([]);
    }
  }, [form.project_id]);

  const handleFileChange = (e) => {
    if (e.target.files?.length) {
      setFiles(prev => [...prev, ...Array.from(e.target.files)]);
    }
  };

  const removeFile = (idx) => {
    setFiles(files.filter((_, i) => i !== idx));
  };

  const submit = async () => {
    if (!form.summary.trim() || !form.project_id) { toast.error("Project and summary required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        assignee_id: form.assignee_id === "unassigned" ? null : form.assignee_id,
        sprint_id: form.sprint_id === "none" ? null : form.sprint_id,
        story_points: form.story_points ? Number(form.story_points) : null,
      };
      const r = await api.post("/issues", payload);

      // Upload attachments if attached
      if (files.length > 0) {
        for (const file of files) {
          const fd = new FormData();
          fd.append("file", file);
          fd.append("issue_id", r.data.id);
          await api.post("/attachments/upload", fd);
        }
      }

      toast.success(`Created ${r.data.key}`);
      setOpen(false);
      setFiles([]);
      onCreated?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-issue-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Create issue</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label>Project</Label>
              <Select value={form.project_id} onValueChange={(v)=>setForm({...form, project_id:v})}>
                <SelectTrigger data-testid="ci-project"><SelectValue placeholder="Select project" /></SelectTrigger>
                <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name} ({p.key})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={form.issue_type} onValueChange={(v)=>setForm({...form, issue_type:v})}>
                <SelectTrigger data-testid="ci-type"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map(t => <SelectItem key={t.id} value={t.id}><div className="flex items-center gap-2"><t.icon className={`w-4 h-4 ${t.cls}`} /> {t.label}</div></SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v)=>setForm({...form, status:v})}>
                <SelectTrigger data-testid="ci-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_LIST.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name || s.label || s.id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Summary</Label>
            <Input value={form.summary} onChange={(e)=>setForm({...form, summary:e.target.value})} placeholder="What needs to be done?" data-testid="ci-summary" autoFocus />
          </div>

          <div className="space-y-1.5">
            <Label>Description</Label>
            <RichTextEditor
              value={form.description}
              onChange={(val) => setForm({ ...form, description: val })}
              placeholder="Add description… (paste or drag & drop images, formatting, / commands, @ mentions)"
              minRows={4}
              users={users}
              testid="ci-description"
            />
          </div>

          <div className="grid grid-cols-4 gap-3">
            <div className="space-y-1.5">
              <Label>Assignee</Label>
              <Select value={form.assignee_id} onValueChange={(v)=>setForm({...form, assignee_id:v})}>
                <SelectTrigger data-testid="ci-assignee"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v)=>setForm({...form, priority:v})}>
                <SelectTrigger data-testid="ci-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIOS.map(p => <SelectItem key={p.id} value={p.id}><div className="flex items-center gap-2"><p.icon className={`w-4 h-4 ${p.cls}`} /> {p.label}</div></SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Sprint</Label>
              <Select value={form.sprint_id || "none"} onValueChange={(v)=>setForm({...form, sprint_id:v})}>
                <SelectTrigger data-testid="ci-sprint"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Backlog / No sprint</SelectItem>
                  {sprints.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name} ({s.state})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Story points</Label>
              <Input type="number" min="0" value={form.story_points} onChange={(e)=>setForm({...form, story_points:e.target.value})} placeholder="—" data-testid="ci-points" />
            </div>
          </div>

          {/* Attachments Upload Field */}
          <div className="space-y-1.5 pt-1">
            <Label className="flex items-center gap-1.5">
              <Paperclip className="w-3.5 h-3.5" /> Attachments
            </Label>
            <div className="border border-dashed border-border rounded-md p-3 bg-muted/20 space-y-2">
              <div className="flex items-center gap-3">
                <Input type="file" multiple onChange={handleFileChange} className="text-xs cursor-pointer h-9" />
              </div>
              {files.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {files.map((f, i) => (
                    <div key={i} className="flex items-center gap-1.5 text-xs bg-background border px-2.5 py-1 rounded-md">
                      <span className="truncate max-w-[180px]">{f.name}</span>
                      <span className="text-muted-foreground text-[10px]">({Math.round(f.size/1024)} KB)</span>
                      <button type="button" onClick={() => removeFile(i)} className="text-muted-foreground hover:text-red-500 ml-1">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={()=>setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="ci-submit">{saving ? "Creating…" : "Create issue"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
